<template lang="html">
  <v-app>
    <!-- channels selector -->
    <v-container fluid class="pa-0 white">
      <v-layout class="unset-center fixed-channels">
        <v-flex xs12>
          <v-btn-toggle mandatory class="btn-toggle-task" v-model="activeChannel">
            <v-btn :disabled = "isChannelAccessBlocked('organizers')" @click="changeActiveChannel('organizers')" value="organizers">
              #Organizers
            </v-btn>
            <v-btn :disabled = "isChannelAccessBlocked('contributors')" @click="changeActiveChannel('contributors')" value="contributors">
              #Contributors
            </v-btn>
            <v-btn :disabled = "isChannelAccessBlocked('prospects')" @click="changeActiveChannel('prospects')" value="prospects">
              #Prospects
            </v-btn>
          </v-btn-toggle>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- chat body -->
    <v-layout column class="body white fixed-body chat-body" @scroll="onChatScroll" ref="chats">
      <span v-if="fetchingChats" class="chat-loader-holder pt-4">
        <loader class="half-circle-spinner-chat"></loader>
      </span>
      <span v-else-if="noMessages" class="chat-loader-holder pt-4">
        <h3>No messages available.</h3>
      </span>

      <v-list dense class="list-container">
        <template v-for="(item, index) in messages">
          <div class="list-tile" :key="item.msg_id" :id="item.msg_id">
            <v-list-tile-avatar>
              <img v-if="getAvatar(item.user_id)" :src="getAvatar(item.user_id)" :alt="getUserName(item.user_id).charAt(0)">
              <v-avatar size="40px" v-else class="red">
                <span class="headline white--text">{{ getUserName(item.user_id).charAt(0).toUpperCase() }}</span>
              </v-avatar>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <div class="title-date-text">
                <span class="title">
                  <router-link class="user-link" :to="{ name: 'UserProfile', params: {id: item.user_id}, query: { ref_task_id: taskId } }">
                    {{ getUserName(item.user_id) }}
                  </router-link>
                </span>
                <span class="date-text"> {{ getTime(item.time_f) }}</span>
              </div>
              <v-list-tile-sub-title>
                {{ item.value | unescape }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </div>
        </template>
        <transition name="fade">
          <div  @click="scrollToTop()"
            v-if="showScrollUp"
            id="scroll-up">
            <v-icon class="upIcon">arrow_upward</v-icon>
          </div>
        </transition>
      </v-list>
    </v-layout>

    <div class="input-container-chat px-3" id="message-input">
      <v-text-field
        single-line
        type="text"
        placeholder="Type here..."
        v-model="message"
        @keyup.enter = "addChatMessage(message)"
      >
      </v-text-field>
    </div>
  </v-app>
</template>

<script>
import moment from 'moment';
import { get, uniqBy } from 'lodash';

import Loader from '@/components/Loader';
import { getChatsViaSocket, addChatMessageViaSocket } from '@/api';

export default {
  components: {
    Loader,
  },
  data() {
    return {
      message: null,
      messages: [],
      activeChannel: null,
      contributors: [],
      timeRange: {},
      fetchingChats: true,
      showScrollUp: false,
    };
  },
  methods: {
    scrollToTop() {
      this.$refs.chats.scrollTop = 0;
    },
    onChatScroll(e) {
      const offsetTop = e.target.scrollTop;
      if (offsetTop === 0 && !this.fetchingChats) {
        const id = document.getElementById(get(this.messages, '[0].msg_id'));
        this.fetchChats(this.activeChannel);
        if (id && this.timeRange.max_time !== '0' && this.timeRange.min_time !== '0') {
          id.scrollIntoView();
        }
      }

      if (offsetTop < 25) {
        this.showScrollUp = false;
      } else {
        this.showScrollUp = true;
      }
    },
    changeActiveChannel(value) {
      // make the messages and timeRange empty on each press
      this.channelCode = null;
      this.fetchingChats = true;
      this.messages = [];
      this.timeRange = {};
      this.fetchChats(value, true);
      this.$router.replace({ ...this.$route, query: { scope: value } });
    },
    getTime(item) {
      const time = moment(item).format('MMM D, hh:mm a');
      return time;
    },
    addChatMessage(message) {
      if (!message || message.trim().length === 0) return;
      const userToken = this.$store.getters['auth/userToken'];

      const values = {
        chat_channel_area: 'project',
        chat_channel_scope: this.activeChannel,
        project_id: this.task.project_id,
        task_id: this.taskId,
        msg: message,
      };

      this.message = null;
      addChatMessageViaSocket(userToken, values, this.handleChatResponse);
    },
    handleChatResponse(response) {
      const data = JSON.parse(response.data);
      const messageChannel = get(data, '[1].data.details.channel_info.chat_channel_code');
      this.channelCode = this.channelCode || messageChannel;

      if (this.channelCode !== messageChannel) {
        return;
      }

      const timeRange = get(data, '[1].data.details.timerange', {});
      this.timeRange = timeRange;
      this.fetchingChats = false;

      // extract contributors, merge them, keep unique
      let contributors = get(data, '[1].data.details.contributors.details', []);
      contributors = [...contributors, ...this.contributors];
      this.contributors = uniqBy(contributors, 'user_id');

      // in time range min or max is 0 then no need to update other items
      if (this.timeRange.min_time === '0' || this.timeRange.max_time === '0') return;
      // extract the message, merge it, only keep uniq ones
      let messages = get(data, '[1].data.details.messages', []);
      messages = [...messages, ...this.messages];
      messages = uniqBy(messages, 'msg_id');
      this.messages = this.sortByDate(messages, 'time_f');
    },
    fetchChats(channelScope, showLatest) {
      const userToken = this.$store.getters['auth/userToken'];
      const minTime = this.timeRange.min_time;
      // when start from becomes 0, no more fetching will be required.
      if (minTime === '0') return;
      this.fetchingChats = true;

      const values = {
        chat_channel_area: 'project',
        chat_channel_scope: channelScope,
        project_id: this.task.project_id,
        task_id: this.taskId,
        before_time: minTime,
      };

      getChatsViaSocket(userToken, values, (response) => {
        this.handleChatResponse(response);
        this.$nextTick(() => {
          if (showLatest) {
            this.scrollBottom();
          }
        });
      });
    },
    scrollBottom() {
      const chatBox = this.$refs.chats;
      chatBox.scrollTop = chatBox.scrollHeight;
    },
    isChannelAccessBlocked(value) {
      if (this.allowedChannelAccess.length === 0) return false;
      for (let i = 0; i < this.allowedChannelAccess.length; i += 1) {
        if (this.allowedChannelAccess[i] === value) {
          return false;
        }
      }
      return true;
    },
    getAvatar(userId) {
      if (!userId) return null;

      let avatar = null;
      for (let i = 0; i < this.contributors.length; i += 1) {
        if (userId === this.contributors[i].user_id) {
          avatar = this.contributors[i].avatar;
        }
      }

      return avatar;
    },
    getUserName(userId) {
      if (!userId) return '';

      let username = '';
      for (let i = 0; i < this.contributors.length; i += 1) {
        if (userId === this.contributors[i].user_id) {
          username = this.contributors[i].username;
        }
      }
      return username;
    },
    sortByDate(messageArray, dateKey) {
      return messageArray.sort((a, b) => (a[dateKey] > b[dateKey] ? 1 : -1));
    },
    setActiveChannel() {
      const scope = this.$route.query.scope;
      if (scope) {
        this.activeChannel = scope;
        return;
      }

      if (this.allowedChannelAccess.length === 0) {
        this.activeChannel = null;
      } else if (this.allowedChannelAccess.includes('organizers')) {
        this.activeChannel = 'organizers';
      } else if (this.allowedChannelAccess.includes('contributors')) {
        this.activeChannel = 'contributors';
      } else if (this.allowedChannelAccess.includes('prospects')) {
        this.activeChannel = 'prospects';
      }
    },
  },
  computed: {
    noMessages() {
      return this.messages.length === 0;
    },
    userId() {
      return this.$store.getters['auth/userId'];
    },
    allowedChannelAccess() {
      // user has access to which channels
      return this.$store.getters['tasks/allowedChannelAccess'];
    },
    taskId() {
      return this.$route.params.id;
    },
    task() {
      return this.$store.getters['tasks/task'];
    },
  },
  // watch: {
  //   activeTabbar(value) {
  //     if (value === 'chat') this.$nextTick(this.scrollBottom);
  //   },
  // },
  mounted() {
    this.$store.dispatch('tasks/resetTasksState');
    this.$nextTick(this.scrollBottom);

    const taskId = this.taskId;
    this.$store.dispatch('tasks/getTaskDetails', taskId).then(() => {
      this.setActiveChannel();
      this.fetchChats(this.activeChannel, true);
    });
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/shared.scss";
#app {
    #message-input {
      z-index: 40;
    }

    .list-container {
      display: flex;
      flex-direction: column;
      margin-top: auto;
    }

    .chat-body {
      display: flex;
      height: auto;
      flex-direction: column;
    }

    .chat-body::-webkit-scrollbar-track {
    	-webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
    	background-color: #F5F5F5;
    }

    .chat-body::-webkit-scrollbar {
    	width: 6px;
    	background-color: #F5F5F5;
    }

    .chat-body::-webkit-scrollbar-thumb {
    	background-color: #b2bac2;
    }

    .fixed-channels {
      position: fixed;
      right: 0;
      left: 0;
      bottom: 65px;
      z-index: 35;
    }

    .fixed-body {
      position: fixed;
      right: 0;
      left: 0;
      bottom: 67px;
      overflow-y: scroll;
      z-index: 35;
    }

    .btn-toggle {
      height: 35px;
      width: 98%;
    }

    @media only screen and (max-width: 599px) {
      .fixed-channels {
        top: 110px;
        height: 25px;
      }

      .fixed-body {
        top: 150px;
      }
    }

    @media only screen and (min-width: 600px) and (max-width: 1600px) {
      .fixed-channels {
        top: 165px;
      }

      .fixed-body {
        top: 220px;
      }
    }


    @media only screen and (min-width: 1600px) {
      .fixed-channels {
        top: 115px;
      }

      .fixed-body {
        top: 165px;
        bottom: 56px;
        left: 300px;
        width: calc(100vw - 320px);
      }
    }

    .chat-loader-holder {
      align-self: center;
    }

    .half-circle-spinner-chat {
      width: 30px;
      height: 30px;
      margin:0px;
    }

    .date-text {
      float: right;
      font-size: 12px;
      color: $header;
    }

    .unset-center {
      text-align: center;
    }

    .list-tile {
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      color: inherit;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      font-size: 16px;
      font-weight: 400;
      min-height: 75px;
      height: auto;
      margin: 0;
      padding-top: 12px;
      padding-bottom: 12px;
      position: relative;
      text-decoration: none;
      -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      border-bottom: 1px solid $lighter;
    }

    .list__tile__avatar {
      margin-bottom: auto;
    }

    .list .list__tile__sub-title, .application .theme--light.list .list__tile__sub-title {
      color: black;
      font-size:13px;
      white-space: inherit;
      word-break: break-word;
    }

    .title-date-text {
      width: 100%;
    }

    .title {
      color: $blue;
      font-size: 13px !important;
    }

    .body {
      padding-top: 20px;
      padding-left: 29px;
      padding-right: 29px;
      padding-bottom: 10px;
    }

    .input-container-chat {
      position: fixed;
      border-color: $border-color;
      border-radius: 8px;
      padding: 14px;
      background-color: #fbfbfb;
      box-shadow: 0 2px 6px 0 rgba(224, 153, 153, 0.5);
      left: 10px;
      bottom: 0;
      width: 98%;
      margin-bottom: 2px;
      input {
        width:100%;
      }
    }

    .input-container-chat input:focus, .input-container textarea:focus {
      outline: none;
      width: 100%;
    }

    @media only screen and (min-width:1600px) {
      .input-container-chat, .btn-toggle {
        width: calc(100% - 325px);
        left: 310px;
        position: fixed;
      }
    }
}

.user-link {
  text-decoration: none;
}

#scroll-up {
  position: fixed;
  right: 30px;
  bottom: 80px;
  z-index: 999;
  cursor:pointer;
}

.upIcon {
  color: $white;
  background: $primary;
  padding: 2px;
  border-radius: 100%;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/TaskDetailsChat.vue